import React, { useState, useMemo, useCallback, useEffect } from 'react';
import Button from '@mui/material/Button';
import Slider from '@mui/material/Slider';
import LinearProgress from '@mui/material/LinearProgress';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Radio from '@mui/material/Radio';
import Pagination from '@mui/material/Pagination';
import SearchIcon from '@mui/icons-material/Search';
import MergeIcon from '@mui/icons-material/CallMerge';

import BoxContainer from '../../style-components/BoxContainer.jsx';
import MergeLearnUnitsModal from '../../style-components/MergeLearnUnitsModal.jsx';
import { getAllImages, mergeDuplicateImages } from '../../models/images';
import { hashImageUrl, hammingNormalized, mapWithConcurrency } from '../../utils/imageHash';

// Concurrencia al leer/hashear imágenes: evita disparar cientos de descargas a
// la vez (que saturarían la red y la memoria).
const HASH_CONCURRENCY = 6;

// Umbral por defecto de la distancia de Hamming perceptual (0 = idénticas).
// Valores bajos => más estricto. ~0.10 detecta duplicados y casi-duplicados.
const DEFAULT_THRESHOLD = 0.1;

// Cantidad de grupos por página (solo afecta el render, no el análisis).
const GROUPS_PER_PAGE = 8;

/**
 * Nombre legible del propietario de una imagen (viene como { firstnames, lastnames }).
 */
function formatOwner(image) {
  if (!image.owner) return 'Sin propietario';
  const name = `${image.owner.firstnames ?? ''} ${image.owner.lastnames ?? ''}`.trim();
  return name || 'Sin propietario';
}

/**
 * Unidad de aprendizaje asociada a una imagen. En producción aún se usa la
 * relación antigua (objeto singular `learnunit`); el respaldo cubre el caso de
 * la nueva relación N:M (`learnUnits`) por si se corre contra el backend nuevo.
 */
function formatLearnunit(image) {
  if (image.learnunit?.name) return image.learnunit.name;
  if (Array.isArray(image.learnUnits) && image.learnUnits.length > 0) {
    return image.learnUnits.map(unit => unit.name).join(', ');
  }
  return 'Sin unidad';
}

/**
 * Reúne, sin duplicados, todas las unidades de aprendizaje presentes en las
 * imágenes de un grupo. Contempla la relación N:M (`learnUnits`, array) y la
 * relación antigua singular (`learnunit`). Solo incluye unidades con id, porque
 * es lo que necesita el backend para reasignarlas a la imagen conservada.
 */
function collectGroupLearnUnits(group) {
  const byId = new Map();
  group.forEach(image => {
    const units = [];
    if (Array.isArray(image.learnUnits)) units.push(...image.learnUnits);
    if (image.learnunit && image.learnunit.id != null) units.push(image.learnunit);
    units.forEach(unit => {
      if (unit && unit.id != null && !byId.has(unit.id)) {
        byId.set(unit.id, { id: unit.id, name: unit.name });
      }
    });
  });
  return [...byId.values()];
}

/**
 * Lee una imagen desde su URL y calcula su hash perceptual (dHash).
 * Si no se puede leer (p. ej. CORS o URL expirada) devuelve hash = null.
 */
async function hashImage(image) {
  return { ...image, hash: await hashImageUrl(image.url) };
}

/**
 * Agrupa imágenes cuyas huellas perceptuales están a una distancia de Hamming
 * <= threshold. Usa union-find para que los duplicados transitivos (A~B, B~C)
 * queden en el mismo grupo. Devuelve solo los grupos con más de un elemento.
 */
function groupDuplicates(hashed, threshold) {
  const valid = hashed.filter(item => item.hash);
  const n = valid.length;
  const parent = Array.from({ length: n }, (_, i) => i);

  const find = x => {
    let root = x;
    while (parent[root] !== root) root = parent[root];
    while (parent[x] !== root) {
      const next = parent[x];
      parent[x] = root;
      x = next;
    }
    return root;
  };
  const union = (a, b) => {
    parent[find(a)] = find(b);
  };

  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      if (hammingNormalized(valid[i].hash, valid[j].hash) <= threshold) {
        union(i, j);
      }
    }
  }

  const groupsByRoot = {};
  for (let i = 0; i < n; i++) {
    const root = find(i);
    if (!groupsByRoot[root]) groupsByRoot[root] = [];
    groupsByRoot[root].push(valid[i]);
  }

  return Object.values(groupsByRoot)
    .filter(group => group.length > 1)
    .sort((a, b) => b.length - a.length);
}

function RepeatedImagesList() {
  const [scanning, setScanning] = useState(false);
  const [scanned, setScanned] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [hashed, setHashed] = useState([]);
  const [threshold, setThreshold] = useState(DEFAULT_THRESHOLD);
  const [error, setError] = useState('');
  // Imagen canónica elegida por grupo (clave = id de la primera imagen del grupo).
  const [selectedCanonical, setSelectedCanonical] = useState({});
  const [mergingKey, setMergingKey] = useState(null);
  const [mergeError, setMergeError] = useState('');
  const [page, setPage] = useState(1);
  // Grupo cuyo modal de fusión está abierto (con su imagen a conservar y las
  // unidades de aprendizaje del grupo). null => modal cerrado.
  const [mergeTarget, setMergeTarget] = useState(null);

  const failedCount = useMemo(() => hashed.filter(item => !item.hash).length, [hashed]);

  // El agrupado es barato: se recalcula al mover el umbral sin re-descargar.
  const groups = useMemo(
    () => (hashed.length ? groupDuplicates(hashed, threshold) : []),
    [hashed, threshold]
  );

  // Paginación solo del render de grupos (el análisis ya se hizo completo).
  const pageCount = Math.max(1, Math.ceil(groups.length / GROUPS_PER_PAGE));
  const pagedGroups = groups.slice((page - 1) * GROUPS_PER_PAGE, page * GROUPS_PER_PAGE);

  // Volver a la primera página cuando cambia el agrupado por el umbral.
  useEffect(() => {
    setPage(1);
  }, [threshold]);

  // Acotar la página si el total de grupos baja (p. ej. al fusionar).
  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const handleScan = useCallback(async () => {
    setScanning(true);
    setScanned(false);
    setError('');
    setHashed([]);
    setProgress({ done: 0, total: 0 });
    setPage(1);

    try {
      const images = await getAllImages();
      if (!images || images.length === 0) {
        setHashed([]);
        setScanned(true);
        return;
      }

      setProgress({ done: 0, total: images.length });
      let done = 0;
      const results = await mapWithConcurrency(images, HASH_CONCURRENCY, async image => {
        const result = await hashImage(image);
        done += 1;
        setProgress({ done, total: images.length });
        return result;
      });

      setHashed(results);
      setScanned(true);
    } catch (err) {
      setError('Ocurrió un error al obtener o procesar las imágenes.');
    } finally {
      setScanning(false);
    }
  }, []);

  // Abrir el modal de fusión: calcula qué imagen se conserva y las unidades de
  // aprendizaje presentes en el grupo (para elegir las de la imagen conservada).
  const openMergeModal = useCallback((group, keepId) => {
    setMergeError('');
    setMergeTarget({
      group,
      keepId,
      learnUnits: collectGroupLearnUnits(group),
    });
  }, []);

  const closeMergeModal = useCallback(() => {
    setMergeTarget(null);
  }, []);

  // Confirmación desde el modal: fusiona el grupo y luego fija en la imagen
  // conservada exactamente las unidades de aprendizaje seleccionadas.
  const handleConfirmMerge = useCallback(
    async selectedLearnUnitIds => {
      if (!mergeTarget) return;
      const { group, keepId } = mergeTarget;
      const deleteIds = group.map(img => img.id).filter(id => id !== keepId);
      if (deleteIds.length === 0) return;

      setMergingKey(group[0].id);
      setMergeError('');

      try {
        // La imagen conservada queda con exactamente las unidades seleccionadas:
        // el propio endpoint de fusión las fija (y elimina las demás) dentro de
        // la misma transacción.
        const response = await mergeDuplicateImages({
          keepId,
          deleteIds,
          learnunitIds: selectedLearnUnitIds,
        });
        if (response?.status === 200) {
          // Quitar las imágenes eliminadas: el grupo desaparece (queda solo la canónica).
          setHashed(prev => prev.filter(item => !deleteIds.includes(item.id)));
          setMergeTarget(null);
        } else {
          setMergeError(response?.response?.data || 'No se pudieron fusionar las imágenes.');
        }
      } catch (e) {
        setMergeError('No se pudieron fusionar las imágenes.');
      } finally {
        setMergingKey(null);
      }
    },
    [mergeTarget]
  );

  const totalImages = hashed.length;
  const analyzedImages = totalImages - failedCount;
  const duplicatesCount = groups.reduce((acc, group) => acc + group.length, 0);

  return (
    <BoxContainer>
      <div className='w-full p-6 sm:p-8'>
        <Typography variant='h5' fontWeight='bold' gutterBottom>
          Imágenes repetidas
        </Typography>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
          Detecta imágenes duplicadas o casi idénticas comparando su contenido visual (huella
          perceptual), sin depender de la URL. El análisis se realiza por completo en el navegador.
        </Typography>

        <div className='flex flex-col sm:flex-row sm:items-center gap-6 mb-6'>
          <Button
            variant='contained'
            startIcon={<SearchIcon />}
            onClick={handleScan}
            disabled={scanning}>
            {scanning ? 'Analizando…' : 'Buscar duplicados'}
          </Button>

          <div className='flex-1 max-w-xs'>
            <Typography variant='caption' color='text.secondary'>
              Sensibilidad (distancia máx.): {threshold.toFixed(2)}
            </Typography>
            <Slider
              value={threshold}
              onChange={(_, value) => setThreshold(value)}
              min={0}
              max={0.3}
              step={0.01}
              size='small'
              disabled={scanning}
              valueLabelDisplay='auto'
            />
            <Typography variant='caption' color='text.secondary'>
              Menor = más estricto (solo casi idénticas). Mayor = agrupa más.
            </Typography>
          </div>
        </div>

        {scanning ? (
          <div className='mb-4'>
            <div className='flex items-center gap-2 mb-1'>
              <CircularProgress size={16} />
              <Typography variant='body2'>
                Procesando {progress.done} / {progress.total} imágenes…
              </Typography>
            </div>
            <LinearProgress
              variant={progress.total ? 'determinate' : 'indeterminate'}
              value={progress.total ? (progress.done / progress.total) * 100 : 0}
            />
          </div>
        ) : null}

        {error ? (
          <Alert severity='error' sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {scanned && !scanning ? (
          <>
            <Typography variant='body2' sx={{ mb: 2 }}>
              Se analizaron <strong>{analyzedImages}</strong> de <strong>{totalImages}</strong>{' '}
              imágenes. Se encontraron <strong>{groups.length}</strong> grupo(s) de repetidas (
              {duplicatesCount} imágenes).
            </Typography>

            {failedCount > 0 ? (
              <Alert severity='warning' sx={{ mb: 2 }}>
                {failedCount} imagen(es) no se pudieron leer. Lo más probable es que el bucket de S3
                aún no permita CORS desde este dominio (sin CORS no se pueden leer los píxeles en el
                navegador, solo mostrarlas).
              </Alert>
            ) : null}

            {mergeError ? (
              <Alert severity='error' sx={{ mb: 2 }} onClose={() => setMergeError('')}>
                {mergeError}
              </Alert>
            ) : null}

            {groups.length === 0 ? (
              <Alert severity='success'>
                No se encontraron imágenes repetidas con la sensibilidad actual.
              </Alert>
            ) : (
              <div className='flex flex-col gap-6'>
                {pagedGroups.map((group, index) => {
                  const groupKey = group[0].id;
                  const keepId = selectedCanonical[groupKey] ?? group[0].id;
                  const isMerging = mergingKey === groupKey;
                  const groupNumber = (page - 1) * GROUPS_PER_PAGE + index + 1;
                  return (
                    <div key={groupKey} className='border border-primary rounded-lg p-4'>
                      <div className='flex items-center justify-between gap-2 mb-1'>
                        <Typography variant='subtitle1' fontWeight='bold'>
                          Grupo {groupNumber} — {group.length} imágenes
                        </Typography>
                        <Button
                          variant='contained'
                          size='small'
                          startIcon={<MergeIcon />}
                          disabled={isMerging}
                          onClick={() => openMergeModal(group, keepId)}>
                          {isMerging ? 'Fusionando…' : 'Fusionar'}
                        </Button>
                      </div>
                      <Typography
                        variant='caption'
                        color='text.secondary'
                        sx={{ mb: 2, display: 'block' }}>
                        Marca la imagen que se conservará; las demás se eliminarán y sus referencias
                        se reasignarán a ella.
                      </Typography>
                      <div className='grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4'>
                        {group.map(image => {
                          const isKept = keepId === image.id;
                          return (
                            <div
                              key={image.id}
                              className={`flex flex-col gap-1 border rounded-md p-2 ${
                                isKept ? 'border-primary bg-primary/5' : 'border-gray-200'
                              }`}>
                              <label className='flex items-center gap-1 cursor-pointer'>
                                <Radio
                                  size='small'
                                  checked={isKept}
                                  onChange={() =>
                                    setSelectedCanonical(prev => ({
                                      ...prev,
                                      [groupKey]: image.id,
                                    }))
                                  }
                                />
                                <Typography
                                  variant='caption'
                                  color={isKept ? 'primary' : 'text.secondary'}>
                                  {isKept ? 'Se conservará' : 'Conservar'}
                                </Typography>
                              </label>
                              <a href={image.url} target='_blank' rel='noreferrer'>
                                <img
                                  src={image.url}
                                  alt={image.details || `Imagen ${image.id}`}
                                  className='w-full h-32 object-contain bg-gray-50 rounded'
                                />
                              </a>
                              <div className='flex flex-col'>
                                <Typography variant='caption' color='text.secondary'>
                                  ID #{image.id}
                                </Typography>
                                <Typography
                                  variant='caption'
                                  className='truncate'
                                  title={image.details}>
                                  {image.details || 'Sin nombre'}
                                </Typography>
                                <Typography
                                  variant='caption'
                                  color='text.secondary'
                                  className='truncate'
                                  title={formatOwner(image)}>
                                  {formatOwner(image)}
                                </Typography>
                                <Typography
                                  variant='caption'
                                  color='text.secondary'
                                  className='truncate'
                                  title={formatLearnunit(image)}>
                                  {formatLearnunit(image)}
                                </Typography>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
                {pageCount > 1 ? (
                  <div className='flex justify-center mt-2'>
                    <Pagination
                      count={pageCount}
                      page={page}
                      onChange={(_, value) => setPage(value)}
                      color='primary'
                    />
                  </div>
                ) : null}
              </div>
            )}
          </>
        ) : null}
      </div>

      <MergeLearnUnitsModal
        open={Boolean(mergeTarget)}
        onClose={closeMergeModal}
        learnUnits={mergeTarget?.learnUnits ?? []}
        keepId={mergeTarget?.keepId}
        deleteCount={
          mergeTarget
            ? mergeTarget.group.filter(img => img.id !== mergeTarget.keepId).length
            : 0
        }
        merging={Boolean(mergeTarget) && mergingKey === mergeTarget.group[0].id}
        onConfirm={handleConfirmMerge}
      />
    </BoxContainer>
  );
}

export default RepeatedImagesList;
